import { useState, useEffect, useCallback, useRef } from 'react';
import { normalizeStatus } from '../utils/webcheckinStatus';
import { fetchCheckIns, fetchCheckInById, updateCheckIn, uploadBoardingPass } from '../services/api';

/**
 * Custom hook for managing web check-ins
 * @param {boolean} isB2bUser - Whether this is for B2B users
 * @returns {Object} Object containing check-in data and methods
 */
const useWebCheckIns = (isB2bUser = false) => {
  const [checkIns, setCheckIns] = useState([]);
  const [sortedData, setSortedData] = useState(null);
  const [selectedCheckIn, setSelectedCheckIn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({
    itemsPerPage: 50,
    pageNumber: 0, // 0-based for new API
    totalItems: 0,
    totalPages: 0
  });

  // Filter states for new API - Set default departureDateMin to today's date
  const [filters, setFilters] = useState(() => {
    const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
    return {
      email: '',
      userId: '',
      checkinStatus: '',
      departureDateMin: today, // Default to today's date
      departureDateMax: '',
      scheduledAtMin: '',
      scheduledAtMax: ''
    };
  });

  // Keep a ref of filters to avoid recreating callbacks and retriggering effects
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Track if initial load has been done to prevent duplicate calls
  const hasInitiallyLoadedRef = useRef(false);
  const lastLoadParamsRef = useRef(null);
  const isB2bUserRef = useRef(isB2bUser);
  const loadingRef = useRef(false);

  // Update ref when isB2bUser changes
  useEffect(() => {
    isB2bUserRef.current = isB2bUser;
  }, [isB2bUser]);

  // Fetch all check-ins with filters
  const loadCheckIns = useCallback(async (page = 0, itemsPerPage = 50, customFilters = {}) => {
    // Prevent concurrent calls
    if (loadingRef.current) {
      console.log('Already loading, skipping duplicate call');
      return;
    }

    setLoading(true);
    loadingRef.current = true;
    setError(null);

    try {
      const params = {
        page: page,
        // Use the most recent filters from the ref to keep this callback stable
        ...filtersRef.current,
        ...customFilters // Allow overriding filters
      };

      // Add isB2bUser parameter if true (either from hook parameter or custom filters)
      if (isB2bUserRef.current || customFilters.isB2bUser) {
        params.isB2bUser = true;
      }

      // Remove empty filter values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      // Check if we're making the same request as before
      const currentParams = JSON.stringify(params);
      if (hasInitiallyLoadedRef.current && lastLoadParamsRef.current === currentParams) {
        console.log('Skipping duplicate API call for check-ins');
        setLoading(false);
        loadingRef.current = false;
        return;
      }

      console.log('Loading check-ins with params:', params);

      const response = await fetchCheckIns(params);

      // Cache the request parameters
      lastLoadParamsRef.current = currentParams;
      hasInitiallyLoadedRef.current = true;
      setCheckIns(response.data);
      setSortedData(response.sortedData);

      // Update pagination info from API response
      setPagination(prev => ({
        ...prev,
        pageNumber: page,
        itemsPerPage: itemsPerPage,
        totalItems: response.pagination.total,
        totalPages: response.pagination.totalPages
      }));
    } catch (err) {
      setError(err.message || 'Failed to load check-ins');
      console.error('Error loading check-ins:', err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []); // Remove dependencies to keep callback stable

  // Load check-ins on mount - but only if we have an auth token and haven't loaded yet
  useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    if (authToken && !hasInitiallyLoadedRef.current) {
      console.log('Auth token found, loading check-ins for the first time');
      loadCheckIns(0, 50); // Use fixed values for initial load
    } else if (!authToken) {
      console.log('No auth token found, skipping initial data load');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Change page
  const changePage = useCallback((newPage) => {
    loadCheckIns(newPage, pagination.itemsPerPage);
  }, [loadCheckIns, pagination.itemsPerPage]);

  // Update filters without triggering API call
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Search with current filters
  const searchWithFilters = useCallback(() => {
    loadCheckIns(0, pagination.itemsPerPage, filters);
  }, [loadCheckIns, pagination.itemsPerPage, filters]);

  // Clear all filters but keep today's date as default for departureDateMin
  const clearFilters = useCallback(() => {
    const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
    const clearedFilters = {
      email: '',
      userId: '',
      checkinStatus: '',
      departureDateMin: today, // Keep today's date as default
      departureDateMax: '',
      scheduledAtMin: '',
      scheduledAtMax: ''
    };
    setFilters(clearedFilters);
    loadCheckIns(0, pagination.itemsPerPage, clearedFilters);
  }, [loadCheckIns, pagination.itemsPerPage]);

  // Get a single check-in by ID
  const getCheckInById = useCallback(async (id) => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchCheckInById(id);
      setSelectedCheckIn(data);
      return data;
    } catch (err) {
      setError(err.message || `Failed to load check-in ${id}`);
      console.error(`Error loading check-in ${id}:`, err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update a check-in
  const updateCheckInData = useCallback(async (id, updates) => {
    setLoading(true);
    setError(null);

    try {
      const updatedCheckIn = await updateCheckIn(id, updates);

      // Update the check-ins list
      setCheckIns(prevCheckIns =>
        prevCheckIns.map(ci => ci.id === id ? { ...ci, ...updates } : ci)
      );

      // Update selected check-in if it's the one being updated
      if (selectedCheckIn && selectedCheckIn.id === id) {
        setSelectedCheckIn({ ...selectedCheckIn, ...updates });
      }

      return updatedCheckIn;
    } catch (err) {
      setError(err.message || `Failed to update check-in ${id}`);
      console.error(`Error updating check-in ${id}:`, err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedCheckIn]);

  // Upload a boarding pass
  const uploadBoardingPassFile = useCallback(async (checkInId, file) => {
    setLoading(true);
    setError(null);

    try {
      const result = await uploadBoardingPass(checkInId, file);

      if (result.success) {
        // Update the check-in with boarding pass info
        await updateCheckInData(checkInId, {
          boardingPass: true,
          status: 'completed'
        });
      }

      return result;
    } catch (err) {
      setError(err.message || `Failed to upload boarding pass for check-in ${checkInId}`);
      console.error(`Error uploading boarding pass for check-in ${checkInId}:`, err);
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
  }, [updateCheckInData]);

  // Filter check-ins by status
  const filteredCheckIns = statusFilter === 'all'
    ? checkIns
    : checkIns.filter(ci => normalizeStatus(ci.status) === statusFilter);

  // Function to get appropriate data array based on sorting
  const getSortedData = (sortBy, sortDirection) => {
    if (!sortedData) {
      return checkIns; // Fallback to regular data if sortedData not available
    }

    // Map sorting criteria to sortedData keys
    if (sortBy === 'departureDate') {
      return sortDirection === 'asc' ? sortedData.departureDateAsc : sortedData.departureDateDesc;
    } else if (sortBy === 'scheduledTime') {
      return sortDirection === 'asc' ? sortedData.scheduledDateAsc : sortedData.scheduledDateDesc;
    }

    // For other sorting criteria, return regular data
    return checkIns;
  };

  return {
    checkIns: filteredCheckIns,
    allCheckIns: checkIns,
    sortedData,
    getSortedData,
    selectedCheckIn,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    pagination,
    changePage,
    loadCheckIns,
    getCheckInById,
    updateCheckIn: updateCheckInData,
    uploadBoardingPass: uploadBoardingPassFile,
    setSelectedCheckIn,
    // New filter functionality
    filters,
    updateFilters,
    searchWithFilters,
    clearFilters
  };
};

export default useWebCheckIns;